/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../../index";
import type { AppSearchResult } from "../shared/types";
import { determineAppMatch } from "../shared/utils";

export const GetAppNameParams = z.object({
  query: z
    .string()
    .describe(
      "The user's natural language query describing the app they want to use (e.g., 'Gmail', 'Google Sheets', 'send emails')",
    ),
  category: z
    .string()
    .optional()
    .describe("Optional category filter to narrow down app search results"),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Finding apps...' or 'Searching integrations...'",
    ),
});

export type GetAppNameParamsType = z.infer<typeof GetAppNameParams>;

export const getAppNameExecute = async (rawArgs: any): Promise<any> => {
  let params: GetAppNameParamsType;
  try {
    params = GetAppNameParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[GET_APP_NAME] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for get_app_name. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { query, category } = params;

  try {
    // Build search parameters
    const searchParams = new URLSearchParams();
    searchParams.append("limit", "10");
    searchParams.append("title_search", query);

    if (category) {
      searchParams.append("category", category);
    }

    // Make the API call through network_request pattern
    const apiUrl = `/api/v4/apps?${searchParams.toString()}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      throw new Error(
        `Apps API returned ${response.status}: ${response.statusText}`,
      );
    }

    const data = await response.json();

    // Handle the response structure (results array)
    const apps: AppSearchResult[] = data.results || [];

    if (apps.length === 0) {
      return {
        $error: {
          message: `No apps found matching "${query}". Please try a different search term or check the app name.`,
        },
      };
    }

    // Determine the best match
    const determination = determineAppMatch(query, apps);

    // If confidence is very low, suggest alternatives
    if (determination.confidence < 0.3 && apps.length > 1) {
      const alternatives = apps
        .slice(0, 3)
        .map((app) => app.name)
        .join(", ");
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(
              {
                app_name: determination.app_name,
                implementation_id: determination.implementation_id,
                confidence: determination.confidence,
                message: `Low confidence match. Did you mean one of: ${alternatives}?`,
                alternatives: apps.slice(0, 3).map((app) => ({
                  name: app.name,
                  implementation_id: app.current_implementation_id,
                })),
              },
              null,
              2,
            ),
          },
        ],
      };
    }

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(
            {
              app_name: determination.app_name,
              implementation_id: determination.implementation_id,
              confidence: determination.confidence,
              total_results: apps.length,
            },
            null,
            2,
          ),
        },
      ],
    };
  } catch (error) {
    console.error("[GET_APP_NAME] Error in execution:", error);
    return {
      $error: {
        message: `Error searching for apps: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const getAppNameDescription = `Determines the correct Zapier app and version based on a user's natural language query.

This tool searches through available Zapier integrations to find the best match for what the user wants to do.

Parameters:
- query: Natural language description (e.g., "Gmail", "Google Sheets", "send emails", "update spreadsheet")
- category: Optional filter to narrow results

Returns:
- app_name: Human-readable name of the app
- implementation_id: Technical app identifier with version (e.g., "GmailCLIAPI@1.5.0")
- confidence: Match confidence score (0-1)

Use this as the first step in field mapping to establish which app integration to work with.`;

export const getAppNameAtom = atom<JotaiTool<z.infer<typeof GetAppNameParams>>>(
  () => ({
    name: "get_app_name",
    description: getAppNameDescription,
    parameters: GetAppNameParams,
    execute: atom(
      null, // read function (not used for write-only atom)
      async (_get, _set, { args }) => {
        return getAppNameExecute(args);
      },
    ),
  }),
);

getAppNameAtom.debugLabel = "getAppNameAtom";

/* eslint-enable sort-exports/sort-exports */
